
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import MainLayout from "@/components/layout/MainLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/components/ui/use-toast";
import ProfileForm from "@/components/signup/ProfileForm";
import { ProfileData } from "@/types/profile";
import { useLanguage } from "@/context/LanguageContext";

const ProfileEdit = () => {
  const [profile, setProfile] = useState<ProfileData | null>(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const { toast } = useToast();
  const { language } = useLanguage();

  useEffect(() => {
    const checkAuth = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        navigate("/login");
        return;
      }

      fetchUserProfile(session.user.id);
    };

    checkAuth();
  }, [navigate]);

  const fetchUserProfile = async (userId: string) => {
    try {
      // 使用 maybeSingle() 避免 "multiple rows returned" 錯誤
      const { data, error } = await supabase
        .from("profiles")
        .select("*")
        .eq("id", userId)
        .maybeSingle();

      console.log("Profile fetch result:", { data, error });

      if (error) {
        console.error("Error fetching profile:", error);
        throw error;
      }

      if (data) {
        const profileData: ProfileData = {
          full_name: data.full_name,
          nickname: data.nickname,
          country: data.country || '',
          age: data.age !== null ? String(data.age) : null,
          gender: data.gender || null
        };
        
        setProfile(profileData);
      } else {
        console.log("No profile found for user");
        // 如果沒有找到 profile，設置空資料讓用戶填寫
        setProfile({
          full_name: "",
          nickname: "",
          country: "",
          age: null,
          gender: null
        });
      }
    } catch (error: any) {
      console.error("Error fetching profile:", error);
      toast({
        variant: "destructive",
        title: language === 'en' ? "Error fetching profile" : "讀取個人資料錯誤",
        description: error.message,
      });
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <MainLayout>
        <div className="container py-8">
          <div className="flex justify-center py-8">
            <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
          </div>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="container py-8 max-w-4xl">
        <Card className="mb-8">
          <CardHeader>
            <CardTitle>{language === 'en' ? 'Edit Profile' : '編輯個人資料'}</CardTitle>
          </CardHeader>
          <CardContent>
            {profile ? (
              <ProfileForm initialData={profile} />
            ) : (
              <p>{language === 'en' ? 'Profile not found' : '找不到個人資料'}</p>
            )}
          </CardContent>
        </Card>
      </div>
    </MainLayout>
  );
};

export default ProfileEdit;
